import React from 'react';
import styled from 'styled-components';
import { ResumeTypeProps } from 'types/types';
import { getFontFamily } from '../../../utils/fontUtils';

const Main = styled.div<{ $fontFamily: string }>`
  font-family: ${props => props.$fontFamily};
  font-size: 0.95rem;
  background: #fff;
  color: black;
`;

const Header = styled.div<{ $fontFamily: string }>`
  font-family: ${props => props.$fontFamily};
  background: #ece8dd;
  padding: 32px 36px 16px 36px;
  text-align: center;
  margin-bottom: 32px;
  box-sizing: border-box;
`;

const Name = styled.h1`
  font-size: 2.4rem;
  letter-spacing: 0.08em;
  margin: 0;
  font-weight: normal;
  color: black;
`;

const Contact = styled.div`
  margin: 12px 0 0 0;
  font-size: 1rem;
  color: #555;
`;

const Section = styled.div`
  margin: 6px 0 0 0;
  padding: 0 8px 8px 8px;
`;

const SectionTitle = styled.h2`
  font-size: 1.1rem;
  letter-spacing: 0.15em;
  text-transform: uppercase;
  text-align: center;
  margin: 0 0 4px 0;
  border-bottom: 2px solid #bdb76b;
  padding-bottom: 2px;
`;

const SubTitle = styled.div`
  font-style: italic;
  color: #555;
`;

const KeyAchievements = styled.div`
  font-style: italic;
  margin-top: 8px;
`;

const BulletList = styled.ul`
  margin: 0 0 0 24px;
  padding: 0;
`;

const EduList = styled.div`
  margin-top: 8px;
  font-size: 0.95rem;
`;

const Email = styled.a`
  color: #555;
  text-decoration: underline dotted;
  font-size: 1rem;
`;

function formatPhone(phone?: string) {
  if (!phone) return '';
  // Remove non-digit characters
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) {
    return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  }
  return phone;
}

function formatDate(dateStr?: string) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleString('en-US', { month: 'long', year: 'numeric' });
}

const StudentClassicResume: React.FC<ResumeTypeProps> = ({ resume }) => {
  const basics = resume.basics || {};
  const education = resume.education || [];
  const work = resume.work || [];
  const volunteer = resume.volunteer || [];
  const skills = resume.skills || [];
  const awards = resume.awards || [];
  const projects = resume.projects || [];
  const certificates = resume.certificates || [];
  const publications = resume.publications || [];
  const languages = resume.languages || [];
  const interests = resume.interests || [];
  const references = resume.references || [];
  const profile = basics.summary || '';

  const sectionsMap: { [key: string]: JSX.Element } = {
    Profile: (
      <Section key="Profile">
        <SectionTitle>Profile</SectionTitle>
        <div style={{ textAlign: 'center' }}>{profile}</div>
      </Section>
    ),
    Skills: (
      <Section key="Skills">
        <SectionTitle>Skills</SectionTitle>
        {skills.map((skill, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <b>{skill.name}</b>
            {/* {skill.level && <span style={{ color: '#555', marginLeft: 8 }}>({skill.level})</span>} */}
            {skill.keywords && skill.keywords.length > 0 && (
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: 4 }}>
                {skill.keywords.join(', ')}
              </div>
            )}
          </div>
        ))}
      </Section>
    ),
    Projects: (
      <Section key="Projects">
        <SectionTitle>Projects</SectionTitle>
        {projects.map((project, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <b>{project.name}</b>
            {project.url && (
              <span style={{ marginLeft: 8 }}>
                <a href={project.url} style={{ color: '#555', textDecoration: 'underline dotted' }}>
                  View Project
                </a>
              </span>
            )}
            {project.startDate && (
              <SubTitle>
                {formatDate(project.startDate)} – {project.endDate ? formatDate(project.endDate) : 'Present'}
              </SubTitle>
            )}
            {project.highlights && project.highlights.length > 0 && (
              <KeyAchievements>
                Key Features:
                <BulletList>
                  {project.highlights.map((h, i) => <li key={i}>{h}</li>)}
                </BulletList>
              </KeyAchievements>
            )}
          </div>
        ))}
      </Section>
    ),
    Awards: (
      <Section key="Awards">
        <SectionTitle>Awards & Honors</SectionTitle>
        {awards.map((award, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <b>{award.title}</b>
            {award.awarder && <SubTitle>{award.awarder}</SubTitle>}
            {award.date && <div>{formatDate(award.date)}</div>}
            {award.summary && <div style={{ marginTop: 4 }}>{award.summary}</div>}
          </div>
        ))}
      </Section>
    ),
    Certificates: (
      <Section key="Certificates">
        <SectionTitle>Certifications</SectionTitle>
        {certificates.map((cert, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <b>{cert.name}</b>
            {cert.issuer && <SubTitle>{cert.issuer}</SubTitle>}
            {cert.date && <div>{formatDate(cert.date)}</div>}
            {cert.url && (
              <div style={{ marginTop: 4 }}>
                <a href={cert.url} style={{ color: '#555', textDecoration: 'underline dotted' }}>
                  View Certificate
                </a>
              </div>
            )}
          </div>
        ))}
      </Section>
    ),
    Publications: (
      <Section key="Publications">
        <SectionTitle>Publications</SectionTitle>
        {publications.map((pub, idx) => (
            <div key={idx} style={{ marginBottom: 8 }}>
            <b>{pub.name}</b>
            {pub.publisher && <SubTitle>{pub.publisher}</SubTitle>}
            {pub.releaseDate && <div>{formatDate(pub.releaseDate)}</div>}
            {pub.url && (
              <div style={{ marginTop: 4 }}>
                <a href={pub.url} style={{ color: '#555', textDecoration: 'underline dotted' }}>
                  View Publication
                </a>
              </div>
            )}
            {pub.summary && <div style={{ marginTop: 4 }}>{pub.summary}</div>}
          </div>
        ))}
      </Section>
    ),
    Languages: (
      <Section key="Languages">
        <SectionTitle>Languages</SectionTitle>
        {languages.map((lang, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <b>{lang.language}</b>
            {lang.fluency && <div style={{ color: '#555', marginLeft: 8 }}>({lang.fluency})</div>}
          </div>
        ))}
      </Section>
    ),
    Interests: (
      <Section key="Interests">
        <SectionTitle>Interests</SectionTitle>
        {interests.map((interest, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <b>{interest.name}</b>
            {interest.keywords && interest.keywords.length > 0 && (
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: 4 }}>
                {interest.keywords.join(', ')}
              </div>
            )}
          </div>
        ))}
      </Section>
    ),
    References: (
      <Section key="References">
        <SectionTitle>References</SectionTitle>
        {references.map((ref, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <b>{ref.name}</b>
            <SubTitle>{ref.reference}</SubTitle>
          </div>
        ))}
      </Section>
    ),
    Volunteer: (
      <Section key="Volunteer">
        <SectionTitle>Volunteer Experience</SectionTitle>
        {volunteer.map((v, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <b>{v.position}</b>
            {v.organization && <SubTitle>{v.organization}</SubTitle>}
            {v.startDate && <div>{formatDate(v.startDate)} – {v.endDate ? formatDate(v.endDate) : 'Present'}</div>}
            {v.highlights && v.highlights.length > 0 && (
              <KeyAchievements>
                Key Achievements:
                <BulletList>
                  {v.highlights.map((h, i) => <li key={i}>{h}</li>)}
                </BulletList>
              </KeyAchievements>
            )}
          </div>
        ))}
      </Section>
    ),
    Work: (
      <Section key="Work">
        <SectionTitle>Work Experience</SectionTitle>
        {work.map((w, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <b>{w.position}</b>
            {w.name && <SubTitle>{w.name}</SubTitle>}
            {w.startDate && <div>{formatDate(w.startDate)} – {w.endDate ? formatDate(w.endDate) : 'Present'}</div>}
            {w.summary && <div>{w.summary}</div>}
            {w.highlights && w.highlights.length > 0 && (
              <KeyAchievements>
                Key Achievements:
                <BulletList>
                  {w.highlights.map((h, i) => <li key={i}>{h}</li>)}
                </BulletList>
              </KeyAchievements>
            )}
          </div>
        ))}
      </Section>
    ),
    Education: (
      <Section key="Education">
        <SectionTitle>Education</SectionTitle>
        {education.map((e, idx) => (
          <div key={idx} style={{ marginBottom: 8 }}>
            <b>{e.institution}</b>
            <div>
              {e.studyType && <span>{e.studyType}</span>}
              {e.area && <span>{e.studyType ? ' in ' : ''}{e.area}</span>}
            </div>
            {e.startDate && (
              <div>
                {formatDate(e.startDate)} – {e.endDate ? formatDate(e.endDate) : 'Present'}
              </div>
            )}
            {e.score && <div>GPA: {e.score}</div>}
            {e.courses && e.courses.length > 0 && (
              <EduList>
                <b>Courses:</b> {e.courses.join(', ')}
              </EduList>
            )}
            {e.highlights && e.highlights.length > 0 && (
              <KeyAchievements>
                Key Achievements:
                <BulletList>
                  {e.highlights.map((h, i) => <li key={i}>{h}</li>)}
                </BulletList>
              </KeyAchievements>
            )}
          </div>
        ))}
      </Section>
    ),
  };

  const sectionOrder = resume.sections || ["Profile", "Education", "Work", "Skills", "Projects", "Volunteer", "Awards"];

  return (
    <Main $fontFamily={getFontFamily(resume.font)}>
      <Header $fontFamily={getFontFamily(resume.font)}>
        <Name>{basics.name}</Name>
        <Contact>
          {basics.location?.city}, {basics.location?.region}
          {basics.phone && (
            <>
              {' '}|{' '}
              <a href={`tel:${basics.phone}`} style={{ color: '#555', textDecoration: 'underline dotted' }}>{formatPhone(basics.phone)}</a>
            </>
          )}
          {basics.email && (
            <>
              {' '}|{' '}
              <Email href={`mailto:${basics.email}`}>{basics.email}</Email>
            </>
          )}
          {basics.url && (
            <>
              {' '}|{' '}
              <a 
                href={basics.url} 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#555', textDecoration: 'underline dotted' }}
              >
                {basics.url}
              </a>
            </>
          )}
        </Contact>
      </Header>
      {sectionOrder.map((section) => sectionsMap[section] || null)}
    </Main>
  );
};

export default StudentClassicResume;
